import type { Point } from './markupShapes.ts';

export interface Size {
    width: number;
    height: number;
}

export interface Viewport extends Size {
    // Screen pixels per image pixel.
    scale: number;
}

export interface ArrowGeometry {
    shaftEnd: Point;
    tip: Point;
    left: Point;
    right: Point;
}

// Small fallback previews would turn to mush past this.
const MAX_FIT_SCALE = 2;
const ARROW_HEAD_LENGTH_PER_WIDTH = 4.5;
const ARROW_HEAD_HALF_WIDTH_RATIO = 0.55;

export function fitImage(image: Size, box: Size, maxScale = MAX_FIT_SCALE): Viewport {
    if (image.width <= 0 || image.height <= 0 || box.width <= 0 || box.height <= 0) {
        return { scale: 0, width: 0, height: 0 };
    }
    const scale = Math.min(box.width / image.width, box.height / image.height, maxScale);
    return {
        scale,
        width: Math.floor(image.width * scale),
        height: Math.floor(image.height * scale)
    };
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

// `screen` is relative to the image's top-left corner on screen.
export function screenToImage(screen: Point, scale: number, image: Size): Point {
    if (scale <= 0) return { x: 0, y: 0 };
    return {
        x: clamp(screen.x / scale, 0, image.width),
        y: clamp(screen.y / scale, 0, image.height)
    };
}

export function imageToScreen(point: Point, scale: number): Point {
    return { x: point.x * scale, y: point.y * scale };
}

export function distance(a: Point, b: Point): number {
    return Math.hypot(b.x - a.x, b.y - a.y);
}

export function appendPenPoint(
    points: readonly Point[],
    point: Point,
    minDistance: number
): readonly Point[] {
    const last = points[points.length - 1];
    if (last && distance(last, point) < minDistance) return points;
    return [...points, point];
}

// Null for a zero-length arrow; a short arrow gets a head no longer than itself.
export function arrowGeometry(from: Point, to: Point, width: number): ArrowGeometry | null {
    const length = distance(from, to);
    if (length === 0) return null;
    const ux = (to.x - from.x) / length;
    const uy = (to.y - from.y) / length;
    const headLength = Math.min(width * ARROW_HEAD_LENGTH_PER_WIDTH, length);
    const halfWidth = headLength * ARROW_HEAD_HALF_WIDTH_RATIO;
    const base = { x: to.x - ux * headLength, y: to.y - uy * headLength };
    return {
        shaftEnd: base,
        tip: to,
        left: { x: base.x - uy * halfWidth, y: base.y + ux * halfWidth },
        right: { x: base.x + uy * halfWidth, y: base.y - ux * halfWidth }
    };
}
